import { NumberField, useFormContext } from '@calendly/form';
import { I18n } from '@calendly/i18n';
import { IconButton } from '@calendly/ui/components/icon-button';
import { CloseMiniIcon } from '@calendly/ui/components/icons';
import { SimpleSelect } from '@calendly/ui/components/select';
import { useDeviceType } from '@calendly/ui/hooks/use-device-type';
import classNames from 'classnames';
import { snakeCase } from 'lodash-es';
import { forwardRef, useRef, useEffect, useImperativeHandle } from 'react';
import { LIMIT_TYPES } from './constants';
const rowV3StyleCss = "r19qgpt3";
const rowCss = "r1cga3et";
const Row = forwardRef((props, ref) => {
  const {
    v3Style,
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(rowCss, {
    [rowV3StyleCss]: v3Style
  }, props.className)} />;
});
const wrapperRefCss = "w1fetlml";
const WrapperRef = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(wrapperRefCss, props.className)} />;
});
const styledNumberFieldIsPhoneCss = "s1kgmp5j";
const styledNumberFieldV3StyleCss = "s1u7hjtj";
const styledNumberFieldCss = "s1ucme8b";
const StyledNumberField = forwardRef((props, ref) => {
  const {
    v3Style,
    ...restProps
  } = props;
  const isPhone = useDeviceType('isPhone');
  return <NumberField ref={ref} {...restProps} className={classNames(styledNumberFieldCss, {
    [styledNumberFieldIsPhoneCss]: isPhone,
    [styledNumberFieldV3StyleCss]: v3Style
  }, props.className)} />;
});
const styledSelectFieldIsPhoneCss = "s1vintly";
const styledSelectFieldCss = "s7r03ql";
const StyledSelectField = forwardRef((props, ref) => {
  const isPhone = useDeviceType('isPhone');
  return <SimpleSelect ref={ref} {...props} className={classNames(styledSelectFieldCss, {
    [styledSelectFieldIsPhoneCss]: isPhone
  }, props.className)} />;
});
const getLimitOptions = () => ({
  dailyLimit: I18n.t('publisher.event_type_editor.sections.scheduling_settings.event_limits.max_meetings.summary.day'),
  weeklyLimit: I18n.t('publisher.event_type_editor.sections.scheduling_settings.event_limits.max_meetings.summary.week'),
  monthlyLimit: I18n.t('publisher.event_type_editor.sections.scheduling_settings.event_limits.max_meetings.summary.month')
});

// i18n-tasks-use t('publisher.event_type_editor.sections.scheduling_settings.event_limits.max_meetings.daily_limit')
// i18n-tasks-use t('publisher.event_type_editor.sections.scheduling_settings.event_limits.max_meetings.weekly_limit')
// i18n-tasks-use t('publisher.event_type_editor.sections.scheduling_settings.event_limits.max_meetings.monthly_limit')
export const LimitField = forwardRef(({
  v3Style,
  limitType,
  observableLimitTypes,
  setObservableLimitTypes,
  onLimitRemoved,
  className
}, ref) => {
  const {
    values: {
      dailyLimit,
      weeklyLimit,
      monthlyLimit
    },
    setFieldValue
  } = useFormContext();
  const limitTypes = {
    dailyLimit,
    weeklyLimit,
    monthlyLimit
  };
  const limitOptions = getLimitOptions();
  const availableOptions = LIMIT_TYPES.filter(type => !observableLimitTypes.includes(type) || type === limitType).map(type => ({
    value: type,
    name: limitOptions[type]
  }));
  const fieldLabel = I18n.t(`publisher.event_type_editor.sections.scheduling_settings.event_limits.max_meetings.${snakeCase(limitType)}`);
  const otherObservableLimitTypes = observableLimitTypes.filter(type => type !== limitType);
  const handleChange = newType => {
    if (newType === limitType) {
      return;
    }
    setFieldValue(newType, limitTypes[limitType]);
    const currentLimitTypeIndex = observableLimitTypes.indexOf(limitType);
    const newObservableLimitTypes = [...observableLimitTypes];
    newObservableLimitTypes.splice(currentLimitTypeIndex, 1, newType);
    setObservableLimitTypes(newObservableLimitTypes);
    setFieldValue(limitType, undefined);
  };
  const handleRemove = () => {
    setFieldValue(limitType, undefined);
    setObservableLimitTypes(otherObservableLimitTypes);
    if (typeof onLimitRemoved === 'function') {
      onLimitRemoved();
    }
  };
  const onFocusInput = e => {
    e.target.select();
  };
  const innerInputRef = useRef(null);
  useImperativeHandle(ref, () => ({
    focus: () => {
      if (innerInputRef.current) {
        innerInputRef.current.focus();
      }
    }
  }), []);
  const wrapperRef = useRef(null);
  useEffect(() => {
    if (!wrapperRef.current) return;
    const realInput = wrapperRef.current.querySelector('input');
    if (realInput) {
      innerInputRef.current = realInput;
    }
  }, []);
  return <Row v3Style={v3Style} className={className}>
      <WrapperRef ref={wrapperRef}>
        <StyledNumberField v3Style={v3Style} name={limitType} aria-label={fieldLabel} onFocus={onFocusInput}>
          <span>
            {I18n.t('publisher.event_type_editor.sections.scheduling_settings.event_limits.max_meetings.meetings_per')}
          </span>
          <StyledSelectField aria-label={I18n.t('publisher.event_type_editor.sections.scheduling_settings.event_limits.max_meetings.limit_period')} disabled={availableOptions.length === 1} onChange={handleChange} options={availableOptions} value={limitType} />
        </StyledNumberField>
      </WrapperRef>
      <div>
        <IconButton size="small" title={I18n.t('publisher.action.remove')} onClick={handleRemove}>
          <CloseMiniIcon size="large" />
        </IconButton>
      </div>
    </Row>;
});

require("./limit_field.wyw-in-js.css!=!../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./limit_field.js");